import { h, setChildren } from "../core/dom.js";
import { api } from "../api.js";
import { navigate } from "../core/router.js";
import { icon } from "../lib/icons.js";
import { topbar, backLink } from "../components/common.js";
import { barChart } from "../components/Chart.js";
import { spinner, errorState, emptyState, toast } from "../components/ui.js";
import { muscleName } from "../store.js";
import { fmtDurationLong, fmtVolume, fmtWeight, weightLabel, toDisplayWeight, fmtDate } from "../lib/format.js";
import { tonnageComparison, monthName, coverage, shareText as buildShareText } from "../lib/yearReview.js";
import { shareText } from "../lib/share.js";

// One scrollable look back over a whole year of per-set data that has otherwise only ever been
// read a week at a time.
//
// The hard constraint is that it has to work for someone with three months of history — the
// common case in an invite-only app. So: nothing is assumed to exist, every superlative card
// disappears rather than printing a hole, and the header says outright how much of the year it's
// actually speaking from. A partial year presented as a year makes every number sound bigger
// than it is, which is the one thing a review like this must not do.
export default function YearReview(params) {
  const root = h("div", { class: "screen" });
  const requested = Number(params?.year);
  load(Number.isInteger(requested) ? requested : null);

  async function load(year) {
    setChildren(root, backLink("Progress", "/history"), spinner());
    try {
      render(await api.yearReview(year));
    } catch (e) {
      setChildren(root, backLink("Progress", "/history"), errorState(e.message, () => load(year)));
    }
  }

  function render(r) {
    if (!r.sessions) {
      setChildren(root,
        backLink("Progress", "/history"),
        topbar(`${r.year} in review`),
        yearPicker(r),
        emptyState({
          icon: "history",
          title: `Nothing logged in ${r.year}`,
          message: r.available_years.length
            ? "Pick another year above."
            : "Finish a workout and your year starts filling in.",
          action: r.available_years.length ? null : h("button", { class: "btn btn-primary", onClick: () => navigate("/start") }, "Start a workout"),
        }));
      return;
    }

    const cov = coverage(r);
    setChildren(root,
      backLink("Progress", "/history"),
      topbar(`${r.year} in review`, { sub: subtitle(r, cov) }),
      yearPicker(r),
      headlineCard(r),
      tonnageCard(r),
      monthsCard(r),
      bestMonthCard(r),
      streakCard(r),
      liftsCard(r),
      musclesCard(r),
      shareButton(r)
    );
  }

  // Says how much of the year this is drawn from, so three months never reads as twelve.
  function subtitle(r, cov) {
    const span = `${cov.monthsTrained} month${cov.monthsTrained === 1 ? "" : "s"} with training`;
    if (!cov.partial) return span;
    return r.year === new Date().getUTCFullYear() ? `${span} · still going` : `${span} · a partial year`;
  }

  function yearPicker(r) {
    const years = r.available_years.length ? r.available_years : [r.year];
    if (years.length < 2) return null;
    return h("div", { class: "shortcut-row", "aria-label": "Choose a year" },
      ...years.map((y) => h("button", {
        class: "shortcut-btn",
        style: y === r.year ? { borderColor: "var(--accent)", color: "var(--accent)" } : null,
        "aria-current": y === r.year ? "true" : null,
        onClick: () => { navigate(`/year/${y}`); },
      }, String(y)))
    );
  }

  function headlineCard(r) {
    return h("div", { class: "stat-grid mt" },
      stat(String(r.sessions), "Workouts"),
      stat(fmtDurationLong(r.duration_seconds), "Time training"),
      stat(String(r.sets), "Sets"),
      stat(String(r.reps), "Reps")
    );
  }

  // The number nobody has intuition for, anchored to one concrete thing. Below the smallest
  // anchor the comparison is omitted rather than rendered as a fraction of an animal.
  function tonnageCard(r) {
    if (!r.volume_kg) return null;
    const cmp = tonnageComparison(r.volume_kg);
    return h("div", { class: "card mt" },
      h("div", { class: "dim", style: { fontSize: ".8rem", fontWeight: 600 } }, "Total lifted"),
      h("div", { class: "num accent", style: { fontSize: "2rem", lineHeight: 1.1, marginTop: "2px" } },
        `${fmtVolume(r.volume_kg).split(" ")[0]} ${weightLabel()}`),
      cmp
        ? h("p", { class: "dim", style: { fontSize: ".88rem", marginTop: "8px", marginBottom: 0, lineHeight: 1.5 } },
            "About ", h("b", null, `${cmp.count} ${cmp.label}`), ", one rep at a time.")
        : null
    );
  }

  function monthsCard(r) {
    // Always twelve bars, zeros included — closing the gaps would draw a year you didn't train.
    const points = r.months.map((m) => ({ x: m.month, y: m.sessions }));
    return h("div", null,
      h("div", { class: "section-title", style: { marginTop: "8px" } }, "Workouts by month"),
      h("div", { class: "card" },
        barChart(points, { format: (v) => `${Math.round(v)}`, xFormat: (m) => monthName(m, { short: true }) })));
  }

  function bestMonthCard(r) {
    if (!r.best_month) return null;
    return h("div", { class: "card mt" },
      h("div", { class: "hstack", style: { gap: "10px", alignItems: "flex-start" } },
        h("div", { style: { color: "var(--accent)", marginTop: "2px" } }, icon("flame", { size: 20 })),
        h("div", { class: "grow" },
          h("div", { style: { fontWeight: 700 } }, `${monthName(r.best_month.month)} was your month`),
          h("div", { class: "dim", style: { fontSize: ".86rem", marginTop: "3px", lineHeight: 1.45 } },
            `${r.best_month.sessions} workouts`,
            r.best_month.volume_kg ? ` · ${fmtVolume(r.best_month.volume_kg)} lifted` : ""))));
  }

  function streakCard(r) {
    // One week on target isn't a streak; saying so would cheapen the ones that are.
    if (r.best_week_streak < 2) return null;
    return h("div", { class: "card mt" },
      h("div", { class: "hstack", style: { gap: "10px", alignItems: "flex-start" } },
        h("div", { style: { color: "var(--accent)", marginTop: "2px" } }, icon("target", { size: 20 })),
        h("div", { class: "grow" },
          h("div", { style: { fontWeight: 700 } }, `${r.best_week_streak} weeks in a row on target`),
          h("div", { class: "dim", style: { fontSize: ".86rem", marginTop: "3px" } },
            `Your longest run of weeks hitting ${r.weekly_goal} workouts.`))));
  }

  function liftsCard(r) {
    if (!r.heaviest_set && !r.best_e1rm && !r.top_exercises.length) return null;
    const rows = [];
    if (r.heaviest_set) {
      rows.push(row("Heaviest set",
        `${toDisplayWeight(r.heaviest_set.weight_kg)}${weightLabel()} × ${r.heaviest_set.reps}`,
        `${r.heaviest_set.exercise_name} · ${fmtDate(r.heaviest_set.started_at)}`));
    }
    if (r.best_e1rm) {
      rows.push(row("Best estimated 1RM", fmtWeight(r.best_e1rm.estimated_1rm_kg),
        `${r.best_e1rm.exercise_name} · ${fmtDate(r.best_e1rm.date)}`));
    }
    if (r.records_standing) {
      rows.push(row("Records still standing", String(r.records_standing),
        `Lifts whose all-time best you set in ${r.year}.`));
    }
    return h("div", null,
      h("div", { class: "section-title", style: { marginTop: "8px" } }, "Your lifts"),
      h("div", { class: "card" }, ...rows,
        r.top_exercises.length
          ? h("div", { style: { marginTop: rows.length ? "12px" : 0 } },
              h("div", { class: "dim", style: { fontSize: ".8rem", fontWeight: 600, marginBottom: "4px" } }, "Most sets"),
              ...r.top_exercises.slice(0, 3).map((e) =>
                h("div", { class: "row-between", style: { padding: "5px 0" } },
                  h("span", null, e.exercise_name),
                  h("span", { class: "dim" }, `${e.sets} sets`))))
          : null));
  }

  function musclesCard(r) {
    // Muscle names come from the store; a muscle the catalog no longer knows is dropped rather
    // than rendered as a blank row.
    const list = r.top_muscles.map((m) => ({ name: muscleName(m.muscle_id), sets: m.sets })).filter((m) => m.name);
    if (!list.length) return null;
    const top = list[0];
    return h("div", null,
      h("div", { class: "section-title", style: { marginTop: "8px" } }, "Where the work went"),
      h("div", { class: "card" },
        h("p", { style: { margin: "0 0 8px", fontWeight: 600 } }, `${top.name} took the most work.`),
        ...list.slice(0, 5).map((m) =>
          h("div", { class: "row-between", style: { padding: "5px 0" } },
            h("span", null, m.name),
            h("span", { class: "dim" }, `${m.sets} sets`)))));
  }

  function shareButton(r) {
    return h("button", {
      class: "btn btn-ghost btn-block mt-lg",
      onClick: async () => {
        const text = buildShareText(r, { fmtWeight, fmtVolume });
        const result = await shareText(text, { title: `My ${r.year} in Kashtu` });
        // Say what actually happened — a share sheet the user dismissed is not a success, and
        // neither is a clipboard write the browser refused.
        if (result === "copied") toast("Copied to your clipboard", "success");
        else if (result === "unavailable") toast("Sharing isn't available on this device", "warn");
      },
    }, icon("forward", { size: 18 }), "Share your year");
  }

  function row(label, value, sub) {
    return h("div", { style: { padding: "7px 0", borderBottom: "1px solid var(--border)" } },
      h("div", { class: "row-between" },
        h("span", { class: "dim", style: { fontSize: ".84rem" } }, label),
        h("span", { style: { fontWeight: 700 } }, value)),
      sub ? h("div", { class: "dim", style: { fontSize: ".78rem", marginTop: "2px" } }, sub) : null);
  }

  function stat(num, label) {
    return h("div", { class: "stat" }, h("div", { class: "num accent" }, num), h("div", { class: "lbl" }, label));
  }

  return root;
}
